import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { IconRemove, Switch } from '@newfront-insurance/core-ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Bot, Download, Loader2, Pencil, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useCustomSkillDownload } from './custom-skill-download';
import { CreateSkillView, getCategoryLabel, getErrorMessage } from './custom-skills-ui';
import { SettingsCard } from './settings-card';
import { useSettingsPanelContextSafe } from './settings-panel-context';
import {
  SkillCategorySection,
  SkillListChevron,
  SkillListExampleUsage,
  SkillListExpandedHint,
  SkillListExpandedSection,
  SkillListExpandedSubLabel,
  SkillListItem,
} from './skill-list-item';
import type { CustomSkillRecord } from '../../../queries/chats';
import { GET_CUSTOM_SKILLS } from '../../../queries/chats';
import { DELETE_CUSTOM_SKILL, UPDATE_CUSTOM_SKILL } from '../../../queries/mutations';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useSkillsSafe } from '../contexts/skills-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';

const ALL_CUSTOM_SKILL_FILTERS = { enabled: [true, false] };
const GENERATE_AI_SUMMARIES = true;

type PendingSkillAction = {
  type: 'toggle' | 'delete';
  uuid: string;
};

function CountBadge({ count }: { count: number | string }) {
  return (
    <span className="inline-flex h-[18px] min-w-7 items-center justify-center rounded-full bg-[#E2EBF1] px-2 text-sm font-semibold tracking-wider text-[#257FFB]">
      {count}
    </span>
  );
}

function getUpdatedLabel(updatedAt: string) {
  return formatDistanceToNow(new Date(updatedAt), { addSuffix: true });
}

function sortCustomSkills(skills: CustomSkillRecord[]) {
  return [...skills].sort((left, right) => {
    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  });
}

function sortSkillsInCategory(skills: CustomSkillRecord[]) {
  return [...skills].sort((left, right) =>
    (left.frontmatter.name || left.command).localeCompare(right.frontmatter.name || right.command),
  );
}

function getCustomSkillDisplayText(...values: Array<string | null | undefined>) {
  for (const value of values) {
    const trimmedValue = value?.trim();
    if (trimmedValue) {
      return trimmedValue;
    }
  }

  return 'No description provided.';
}

function groupCustomSkills(skills: CustomSkillRecord[]) {
  const grouped = new Map<string, CustomSkillRecord[]>();

  for (const skill of skills) {
    const category = getCategoryLabel(skill);
    const current = grouped.get(category) ?? [];
    current.push(skill);
    grouped.set(category, current);
  }

  return [...grouped.entries()]
    .sort(([left], [right]) => {
      if (left === 'Uncategorized') {
        return 1;
      }

      if (right === 'Uncategorized') {
        return -1;
      }

      return left.localeCompare(right);
    })
    .map(([category, groupSkills]) => ({
      category,
      skills: sortSkillsInCategory(groupSkills),
    }));
}

function InlineIconButton({
  children,
  label,
  disabled,
  onClick,
  testId,
  ariaBusy,
}: {
  children: ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  ariaBusy?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-busy={ariaBusy}
      title={label}
      className="inline-flex h-6 w-6 items-center justify-center p-1 text-[#546A83] transition-colors hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function CustomSkillsManageView({
  onBack,
  embedded = false,
  onAddSkill,
  onEditSkill,
}: {
  onBack?: () => void;
  embedded?: boolean;
  onAddSkill?: () => void;
  onEditSkill?: (skill: CustomSkillRecord) => void;
}) {
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const notify = useNotify();
  const skillsContext = useSkillsSafe();
  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);
  const [skillToUpdate, setSkillToUpdate] = useState<CustomSkillRecord | null>(null);
  const [expandedSkillUuids, setExpandedSkillUuids] = useState<string[]>([]);
  const [pendingAction, setPendingAction] = useState<PendingSkillAction | null>(null);
  const [skillToDelete, setSkillToDelete] = useState<CustomSkillRecord | null>(null);
  const { downloadCustomSkill, isDownloadingCustomSkill } = useCustomSkillDownload();

  const {
    data,
    loading,
    error,
    refetch: refetchCustomSkills,
  } = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: ALL_CUSTOM_SKILL_FILTERS },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const [updateCustomSkill] = useMutation(UPDATE_CUSTOM_SKILL);
  const [deleteCustomSkill] = useMutation(DELETE_CUSTOM_SKILL);

  const skills = useMemo(() => sortCustomSkills(data?.customSkills ?? []), [data?.customSkills]);
  const groupedSkills = useMemo(() => groupCustomSkills(skills), [skills]);
  const runtimeSkillsByUuid = useMemo(
    () => new Map((skillsContext?.skills ?? []).map((skill) => [skill.id, skill] as const)),
    [skillsContext?.skills],
  );
  const isInitialLoading = loading && !data;

  const refreshRuntimeSkills = useCallback(async () => {
    await skillsContext?.refetchSkills();
  }, [skillsContext]);

  const reloadSkills = useCallback(async () => {
    await refetchCustomSkills({ filters: ALL_CUSTOM_SKILL_FILTERS });
  }, [refetchCustomSkills]);

  const openCreateView = useCallback(() => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ADD_SKILL_CLICKED, {
      chatUuid: chatOverlayContext?.chatUuid,
      source: 'settings',
    });

    if (onAddSkill) {
      onAddSkill();
      return;
    }

    setSkillToUpdate(null);
    setIsCreateViewOpen(true);
  }, [analytics, chatOverlayContext?.chatUuid, onAddSkill]);

  const openUpdateView = useCallback(
    (skill: CustomSkillRecord) => {
      if (onEditSkill) {
        onEditSkill(skill);
        return;
      }

      setIsCreateViewOpen(false);
      setSkillToUpdate(skill);
    },
    [onEditSkill],
  );

  const isSkillPending = useCallback(
    (uuid: string) => {
      return pendingAction?.uuid === uuid;
    },
    [pendingAction],
  );

  const isSkillExpanded = useCallback(
    (uuid: string) => {
      return expandedSkillUuids.includes(uuid);
    },
    [expandedSkillUuids],
  );

  const toggleExpandedSkill = useCallback((uuid: string) => {
    setExpandedSkillUuids((currentUuids) =>
      currentUuids.includes(uuid)
        ? currentUuids.filter((currentUuid) => currentUuid !== uuid)
        : [...currentUuids, uuid],
    );
  }, []);

  const handleToggle = useCallback(
    async (skill: CustomSkillRecord) => {
      setPendingAction({ type: 'toggle', uuid: skill.uuid });
      try {
        await updateCustomSkill({
          variables: {
            uuid: skill.uuid,
            command: {
              enabled: !skill.enabled,
              generateAiSummaries: GENERATE_AI_SUMMARIES,
            },
          },
        });
        await reloadSkills();
        await refreshRuntimeSkills();
        notify.success(skill.enabled ? 'Custom skill disabled.' : 'Custom skill enabled.');
      } catch (toggleError) {
        notify.error(getErrorMessage(toggleError, 'Unable to update custom skill.'));
      } finally {
        setPendingAction(null);
      }
    },
    [notify, refreshRuntimeSkills, reloadSkills, updateCustomSkill],
  );

  const handleDelete = async () => {
    if (!skillToDelete) {
      return;
    }

    setPendingAction({ type: 'delete', uuid: skillToDelete.uuid });
    try {
      await deleteCustomSkill({
        variables: {
          uuid: skillToDelete.uuid,
        },
      });
      await reloadSkills();
      await refreshRuntimeSkills();
      notify.success('Custom skill deleted.');
      setSkillToDelete(null);
    } catch (deleteError) {
      notify.error(getErrorMessage(deleteError, 'Unable to delete custom skill.'));
    } finally {
      setPendingAction(null);
    }
  };

  const renderSkillRow = useCallback(
    (skill: CustomSkillRecord, category: string) => {
      const isDownloading = isDownloadingCustomSkill(skill.uuid);
      const isPending = isSkillPending(skill.uuid) || isDownloading;
      const isExpanded = isSkillExpanded(skill.uuid);
      const runtimeSkill = runtimeSkillsByUuid.get(skill.uuid);
      const variant = embedded ? 'compact' : 'default';

      return (
        <SkillListItem
          key={skill.uuid}
          handle={`/${skill.command}`}
          description={getCustomSkillDisplayText(skill.frontmatter.summary, skill.frontmatter.description)}
          icon={embedded ? undefined : Bot}
          variant={variant}
          isExpanded={isExpanded}
          isDisabled={pendingAction?.type === 'delete' && pendingAction.uuid === skill.uuid}
          onPrimaryAction={() => toggleExpandedSkill(skill.uuid)}
          primaryActionAriaLabel={
            isExpanded ? `Collapse details for ${skill.frontmatter.name}` : `Open details for ${skill.frontmatter.name}`
          }
          inlineActions={
            <>
              {embedded ? null : (
                <Switch enabled={skill.enabled} onClick={() => void handleToggle(skill)} locked={isPending} />
              )}

              {skill.sourceFileUuid ? (
                <InlineIconButton
                  label={`Download ${skill.frontmatter.name}`}
                  disabled={isPending}
                  onClick={() => void downloadCustomSkill(skill)}
                  testId="download-skill-button"
                  ariaBusy={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-[#546A83]" />
                  ) : (
                    <Download aria-hidden="true" className="h-4 w-4 text-[#546A83]" />
                  )}
                </InlineIconButton>
              ) : null}

              <InlineIconButton
                label={`Edit ${skill.frontmatter.name}`}
                disabled={isPending}
                onClick={() => openUpdateView(skill)}
              >
                <Pencil aria-hidden="true" className="h-4 w-4" />
              </InlineIconButton>

              <InlineIconButton
                label={`Delete ${skill.frontmatter.name}`}
                disabled={isPending}
                onClick={() => setSkillToDelete(skill)}
              >
                <span aria-hidden="true">
                  <IconRemove color="currentColor" size={16} />
                </span>
              </InlineIconButton>
            </>
          }
          trailingAction={
            <SkillListChevron
              isExpanded={isExpanded}
              onClick={() => toggleExpandedSkill(skill.uuid)}
              label={isExpanded ? `Collapse ${skill.frontmatter.name}` : `Expand ${skill.frontmatter.name}`}
              disabled={isPending}
              variant={variant}
            />
          }
          expandedContent={
            <>
              {embedded ? (
                <div className="flex items-center gap-2 px-1 py-0.5">
                  <span className="text-[12px] leading-4 text-[#546A83]">
                    {skill.enabled ? 'Disable skill' : 'Enable skill'}
                  </span>
                  <Switch enabled={skill.enabled} onClick={() => void handleToggle(skill)} locked={isPending} />
                </div>
              ) : null}

              <SkillListExpandedSection label={embedded ? 'What it does' : 'Skill details'} variant={variant}>
                {embedded ? (
                  <SkillListExpandedHint variant={variant}>
                    {getCustomSkillDisplayText(skill.frontmatter.overview, skill.frontmatter.description)}
                  </SkillListExpandedHint>
                ) : (
                  <div className="flex min-w-0 flex-col gap-1">
                    <SkillListExpandedSubLabel variant={variant}>
                      Use `/{skill.command}` to load this skill
                    </SkillListExpandedSubLabel>
                    <div className="mt-2">
                      <SkillListExpandedHint variant={variant}>
                        {getCustomSkillDisplayText(skill.frontmatter.overview, skill.frontmatter.description)}
                      </SkillListExpandedHint>
                    </div>
                  </div>
                )}
              </SkillListExpandedSection>

              {embedded && runtimeSkill?.exampleUsage ? (
                <SkillListExpandedSection label="Example usage" variant={variant}>
                  <SkillListExampleUsage handle={skill.command} variant={variant}>
                    {runtimeSkill.exampleUsage}
                  </SkillListExampleUsage>
                </SkillListExpandedSection>
              ) : null}

              {embedded ? null : (
                <SkillListExpandedSection label="Metadata" variant={variant}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[#DEE2E7] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#546A83]">
                      {category}
                    </span>
                    <span className="rounded-full border border-[#DEE2E7] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#546A83]">
                      {skill.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <span className="rounded-full border border-[#DEE2E7] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#546A83]">
                      Updated {getUpdatedLabel(skill.updatedAt)}
                    </span>
                  </div>
                </SkillListExpandedSection>
              )}
            </>
          }
        />
      );
    },
    [
      downloadCustomSkill,
      embedded,
      handleToggle,
      isDownloadingCustomSkill,
      isSkillExpanded,
      isSkillPending,
      openUpdateView,
      pendingAction,
      runtimeSkillsByUuid,
      toggleExpandedSkill,
    ],
  );

  if (isCreateViewOpen) {
    return <CreateSkillView onBack={() => setIsCreateViewOpen(false)} />;
  }

  if (skillToUpdate) {
    return <CreateSkillView onBack={() => setSkillToUpdate(null)} skillToUpdate={skillToUpdate} />;
  }

  return (
    <div className={`flex min-h-0 flex-col ${embedded ? '' : 'h-full'}`}>
      {embedded ? null : (
        <div className="border-border flex flex-wrap items-start justify-between gap-4 border-b px-5 py-4">
          <div className="flex min-w-0 flex-col gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="text-brand-400 inline-flex items-center gap-1 text-sm font-semibold"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : null}
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Custom skills</h3>
              <CountBadge count={skills.length} />
            </div>
            <p className="text-muted-foreground text-sm">
              Upload ZIP bundles, enable or disable them, and remove custom skills from your Nova account.
            </p>
          </div>

          <Button type="button" className="min-w-[160px]" onClick={openCreateView}>
            <Plus className="mr-2 h-4 w-4" />
            Add new skill
          </Button>
        </div>
      )}

      <div className={`flex-1 overflow-y-auto ${embedded ? '' : 'px-5 py-4'}`}>
        <div className="flex flex-col gap-4">
          {isInitialLoading ? (
            <div className={`flex items-center justify-center ${embedded ? 'px-5 py-8' : 'py-10'}`}>
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              <span className="text-muted-foreground ml-2 text-sm">Loading custom skills...</span>
            </div>
          ) : error ? (
            <div className={`border-border bg-background rounded-md border p-4 ${embedded ? 'mx-5 my-5' : ''}`}>
              <p className="text-destructive text-sm">{getErrorMessage(error, 'Unable to load custom skills.')}</p>
              <Button type="button" className="mt-3" onClick={() => void reloadSkills()}>
                Retry
              </Button>
            </div>
          ) : skills.length === 0 ? (
            <div className={`${embedded ? 'mx-5 my-3' : ''} px-1 py-1`}>
              <p className="text-muted-foreground text-sm leading-6">
                No custom skills yet. Add one from the header above or use{' '}
                <span className="font-mono">/skill-creator</span> in chat to draft a new skill.
              </p>
            </div>
          ) : (
            <div className={embedded ? '' : 'border-border bg-background overflow-hidden rounded-[4px] border'}>
              {embedded ? null : (
                <div className="border-b border-[#BEC7D0] px-5 py-4">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold">Custom skills</p>
                    <p className="text-muted-foreground text-xs">
                      Review details, organize categories, and manage availability from one list.
                    </p>
                  </div>
                </div>
              )}

              {embedded
                ? skills.map((skill) => renderSkillRow(skill, getCategoryLabel(skill)))
                : groupedSkills.map((group) => (
                    <SkillCategorySection
                      key={group.category}
                      title={group.category}
                      countLabel={`${group.skills.length} ${group.skills.length === 1 ? 'skill' : 'skills'}`}
                    >
                      {group.skills.map((skill) => renderSkillRow(skill, group.category))}
                    </SkillCategorySection>
                  ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={Boolean(skillToDelete)} onOpenChange={(open) => !open && setSkillToDelete(null)}>
        <AlertDialogContent className="w-[349px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom skill</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {skillToDelete ? (
              <>
                This removes <span className="font-mono font-semibold text-[#257FFB]">/{skillToDelete.command}</span>{' '}
                from your custom skills list.
              </>
            ) : (
              ''
            )}
          </AlertDialogDescription>
          <AlertDialogFooter className="flex-row justify-end">
            <AlertDialogCancel disabled={pendingAction?.type === 'delete'}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="dangerous"
              disabled={pendingAction?.type === 'delete'}
              onClick={() => void handleDelete()}
            >
              {pendingAction?.type === 'delete' ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function CustomSkillsSettings() {
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const isNovaSkillsManagementEnabled = useFeatureFlag('nova-skills-management', false);
  const settingsPanel = useSettingsPanelContextSafe();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();

  if (!isNovaSkillsEnabled || !isNovaSkillsManagementEnabled) {
    return null;
  }

  const openCreateView = () => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ADD_SKILL_CLICKED, {
      chatUuid: chatOverlayContext?.chatUuid,
      source: 'settings',
    });

    if (!settingsPanel) {
      return;
    }

    settingsPanel.pushView(<CreateSkillView onBack={settingsPanel.popView} />);
  };

  const openUpdateView = (skill: CustomSkillRecord) => {
    if (!settingsPanel) {
      return;
    }

    settingsPanel.pushView(<CreateSkillView onBack={settingsPanel.popView} skillToUpdate={skill} />);
  };

  return (
    <SettingsCard
      title="Custom skills"
      action={
        <button
          type="button"
          onClick={openCreateView}
          className="text-brand-400 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold"
        >
          Add new skill
        </button>
      }
      flushContent
    >
      <CustomSkillsManageView embedded onAddSkill={openCreateView} onEditSkill={openUpdateView} />
    </SettingsCard>
  );
}
